import { useCallback, useEffect, useRef, useState } from 'react';

// Unified audio feedback for the assistant: paced TTS + synthesized earcons, one shared mute.
// Design and all timing/frequency parameters come from the UX panel synthesis (2026-07):
// - A running utterance is NEVER cut mid-word — with two exceptions: 'blocker' announcements
//   interrupt everything, and event 'feedback' interrupts a running INSTRUCTION (the moment a
//   meter is matched or a value lands, the old instruction is obsolete; feedback spoken seconds
//   late reads as a second, phantom event). A running feedback is never cut.
// - Between utterances there is a fixed silence gap — the "beat" the flow was missing.
// - New instructions coalesce (only the latest pending instruction is spoken); feedback snippets
//   ("Zähler erkannt!") are never replaced and run before the next instruction.
// - Earcons are oscillator-synthesized (no assets); the AudioContext is unlocked in prime()
//   within the same user gesture that unlocks speechSynthesis (iOS requirement).

const MUTED_STORAGE_KEY = 'aiReading.audioMuted';
// Pre-unification key ("TTS only") — read as fallback so existing users keep their preference.
const LEGACY_MUTED_STORAGE_KEY = 'aiReading.ttsMuted';

const INTER_UTTERANCE_GAP_MS = 900;
// iOS swallows an utterance queued immediately after cancel() — give it a moment to settle.
const CANCEL_SETTLE_MS = 250;
// utterance.onend is unreliable on iOS — estimate the duration from the text length instead.
const FALLBACK_MS_PER_CHAR = 70;
const FALLBACK_MIN_MS = 1500;
const FALLBACK_MAX_MS = 8000;
// Gap between an earcon and the speech belonging to the same event (tone first, then words).
export const EARCON_TO_SPEECH_MS = 300;

export type SpeakKind = 'instruction' | 'feedback' | 'blocker';
export type EarconName = 'success' | 'tick' | 'complete' | 'snap' | 'error';

interface EarconNote {
    freq: number;
    // Seconds relative to the earcon start / note duration in seconds.
    at: number;
    dur: number;
    type: OscillatorType;
    gain: number;
}

const EARCONS: Record<EarconName, EarconNote[]> = {
    // Rising fifth E5→A5 — the "meter identified" success moment.
    success: [
        { freq: 659, at: 0, dur: 0.12, type: 'sine', gain: 0.18 },
        { freq: 880, at: 0.12, dur: 0.14, type: 'sine', gain: 0.18 },
    ],
    // Short bright triangle blip — a register value was captured / stepper advanced.
    tick: [{ freq: 1175, at: 0, dur: 0.07, type: 'triangle', gain: 0.12 }],
    // C5 major arpeggio — everything captured / submitted.
    complete: [
        { freq: 523, at: 0, dur: 0.09, type: 'sine', gain: 0.15 },
        { freq: 659, at: 0.12, dur: 0.09, type: 'sine', gain: 0.15 },
        { freq: 784, at: 0.24, dur: 0.09, type: 'sine', gain: 0.15 },
    ],
    // Subtle single blip when the target frame is reached.
    snap: [{ freq: 880, at: 0, dur: 0.06, type: 'sine', gain: 0.1 }],
    // Two low pulses — blocker (meter not found / offline).
    error: [
        { freq: 220, at: 0, dur: 0.15, type: 'sine', gain: 0.12 },
        { freq: 220, at: 0.23, dur: 0.15, type: 'sine', gain: 0.12 },
    ],
};

const readMuted = (): boolean => {
    try {
        const value = localStorage.getItem(MUTED_STORAGE_KEY) ?? localStorage.getItem(LEGACY_MUTED_STORAGE_KEY);
        return value === 'true';
    } catch {
        return false;
    }
};

// The app's language codes carry a form-of-address suffix ('en-FORMAL', 'de-informal') that is
// no BCP 47 region — only the primary subtag is meaningful for speech. Unknown languages pass
// their primary subtag through and let the browser pick.
const SPEECH_LOCALES: Record<string, string> = { de: 'de-DE', en: 'en-US' };
const DEFAULT_SPEECH_LANG = 'de-DE';

export const toSpeechLang = (language?: string): string => {
    const primary = (language ?? '').split('-')[0].toLowerCase();
    if (!primary) {
        return DEFAULT_SPEECH_LANG;
    }
    return SPEECH_LOCALES[primary] ?? primary;
};

// Android reports voice locales as 'en_US'.
const normalizeLang = (lang: string) => lang.replace('_', '-').toLowerCase();

// Setting utterance.lang alone is ignored by iOS Safari (it keeps the system voice), so an
// explicit voice is chosen: exact locale first, then any voice of the same language.
export const pickVoice = (voices: SpeechSynthesisVoice[], speechLang: string): SpeechSynthesisVoice | undefined => {
    const wanted = normalizeLang(speechLang);
    const primary = wanted.split('-')[0];
    return (
        voices.find((voice) => normalizeLang(voice.lang) === wanted) ??
        voices.find((voice) => normalizeLang(voice.lang).split('-')[0] === primary)
    );
};

const synthAvailable = () => typeof window !== 'undefined' && 'speechSynthesis' in window && typeof (window as any).SpeechSynthesisUtterance === 'function';

interface QueueItem {
    text: string;
    kind: SpeakKind;
}

export const useAudioFeedback = (language?: string) => {
    const [muted, setMuted] = useState<boolean>(readMuted);
    const mutedRef = useRef(muted);
    mutedRef.current = muted;
    const speechLangRef = useRef(toSpeechLang(language));
    speechLangRef.current = toSpeechLang(language);
    const voicesRef = useRef<SpeechSynthesisVoice[]>([]);

    const audioCtxRef = useRef<AudioContext | null>(null);
    const speakingRef = useRef(false);
    const queueRef = useRef<QueueItem[]>([]);
    const gapTimerRef = useRef<number | null>(null);
    const settleTimerRef = useRef<number | null>(null);
    const fallbackTimerRef = useRef<number | null>(null);
    const currentTextRef = useRef<string | null>(null);
    const currentKindRef = useRef<SpeakKind | null>(null);
    const currentUtteranceRef = useRef<any>(null);

    const clearTimers = useCallback(() => {
        for (const ref of [gapTimerRef, settleTimerRef, fallbackTimerRef]) {
            if (ref.current != null) {
                window.clearTimeout(ref.current);
                ref.current = null;
            }
        }
    }, []);

    const startUtterance = useCallback((text: string, kind: SpeakKind = 'instruction') => {
        speakingRef.current = true;
        currentTextRef.current = text;
        currentKindRef.current = kind;

        let finished = false;
        const finish = () => {
            if (finished) {
                return;
            }
            finished = true;
            if (fallbackTimerRef.current != null) {
                window.clearTimeout(fallbackTimerRef.current);
                fallbackTimerRef.current = null;
            }
            speakingRef.current = false;
            // The inter-utterance beat: fixed silence before whatever is queued next.
            gapTimerRef.current = window.setTimeout(() => {
                gapTimerRef.current = null;
                const next = queueRef.current.shift();
                if (next) {
                    startUtterance(next.text, next.kind);
                }
            }, INTER_UTTERANCE_GAP_MS);
        };

        const utterance = new (window as any).SpeechSynthesisUtterance(text);
        utterance.lang = speechLangRef.current;
        if (voicesRef.current.length === 0) {
            voicesRef.current = window.speechSynthesis.getVoices?.() ?? [];
        }
        const voice = pickVoice(voicesRef.current, speechLangRef.current);
        if (voice) {
            utterance.voice = voice;
        }
        utterance.onend = finish;
        utterance.onerror = finish;
        currentUtteranceRef.current = utterance;
        const estimated = Math.min(FALLBACK_MAX_MS, Math.max(FALLBACK_MIN_MS, text.length * FALLBACK_MS_PER_CHAR));
        fallbackTimerRef.current = window.setTimeout(finish, estimated);
        window.speechSynthesis.speak(utterance);
    }, []);

    // Cancels the running utterance WITHOUT letting its onend/onerror (which cancel() triggers)
    // restart the queue — the caller owns what happens next (settle window, then its own start).
    const cancelCurrentUtterance = useCallback(() => {
        const current = currentUtteranceRef.current;
        if (current) {
            current.onend = null;
            current.onerror = null;
        }
        window.speechSynthesis.cancel();
    }, []);

    const speak = useCallback(
        (text: string, kind: SpeakKind = 'instruction') => {
            if (mutedRef.current || !synthAvailable() || !text) {
                return;
            }

            if (kind === 'blocker') {
                // Blockers (offline, meter not found, permission) preempt everything immediately.
                queueRef.current = [];
                clearTimers();
                cancelCurrentUtterance();
                speakingRef.current = true; // occupy the channel during the settle window
                settleTimerRef.current = window.setTimeout(() => {
                    settleTimerRef.current = null;
                    startUtterance(text, kind);
                }, CANCEL_SETTLE_MS);
                return;
            }

            // Event feedback preempts a running INSTRUCTION (never a running feedback): the
            // instruction is obsolete the moment the event happened, and feedback spoken seconds
            // late reads as a second, phantom event. Unlike blockers the queue survives.
            if (kind === 'feedback' && speakingRef.current && currentKindRef.current === 'instruction') {
                clearTimers();
                cancelCurrentUtterance();
                speakingRef.current = true; // occupy the channel during the settle window
                settleTimerRef.current = window.setTimeout(() => {
                    settleTimerRef.current = null;
                    startUtterance(text, kind);
                }, CANCEL_SETTLE_MS);
                return;
            }

            const busy = speakingRef.current || gapTimerRef.current != null || settleTimerRef.current != null;
            if (!busy) {
                startUtterance(text, kind);
                return;
            }

            // Don't restart what is already being said or already waiting.
            if (currentTextRef.current === text || queueRef.current.some((item) => item.text === text)) {
                return;
            }
            if (kind === 'instruction') {
                // Coalesce: only the LATEST instruction survives, always at the end of the queue
                // (feedback snippets stay in front of it).
                queueRef.current = queueRef.current.filter((item) => item.kind !== 'instruction');
                queueRef.current.push({ text, kind });
            } else {
                // Feedback runs before the next pending instruction, in arrival order.
                const instructionIndex = queueRef.current.findIndex((item) => item.kind === 'instruction');
                if (instructionIndex === -1) {
                    queueRef.current.push({ text, kind });
                } else {
                    queueRef.current.splice(instructionIndex, 0, { text, kind });
                }
            }
        },
        [clearTimers, startUtterance, cancelCurrentUtterance],
    );

    // iOS unlocks speechSynthesis AND WebAudio only inside a user gesture — call prime() in the
    // start button's onClick. The AudioContext is created even while muted, so unmuting
    // mid-session still produces sound.
    const prime = useCallback(() => {
        if (synthAvailable()) {
            const utterance = new (window as any).SpeechSynthesisUtterance('');
            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(utterance);
        }
        try {
            const Ctor = (window as any).AudioContext ?? (window as any).webkitAudioContext;
            if (Ctor && !audioCtxRef.current) {
                audioCtxRef.current = new Ctor();
            }
            const ctx = audioCtxRef.current;
            if (ctx) {
                ctx.resume?.();
                // 1-sample silent buffer completes the iOS unlock within this gesture.
                const buffer = ctx.createBuffer(1, 1, 22050);
                const source = ctx.createBufferSource();
                source.buffer = buffer;
                source.connect(ctx.destination);
                source.start(0);
            }
        } catch {
            // No WebAudio — earcons silently unavailable, TTS still works.
        }
    }, []);

    const playEarcon = useCallback((name: EarconName) => {
        if (mutedRef.current) {
            return;
        }
        const ctx = audioCtxRef.current;
        if (!ctx) {
            return;
        }
        try {
            if (ctx.state === 'suspended') {
                ctx.resume?.();
            }
            const start = ctx.currentTime + 0.01;
            for (const note of EARCONS[name]) {
                const oscillator = ctx.createOscillator();
                const gain = ctx.createGain();
                oscillator.type = note.type;
                oscillator.frequency.value = note.freq;
                const t0 = start + note.at;
                gain.gain.setValueAtTime(0, t0);
                gain.gain.linearRampToValueAtTime(note.gain, t0 + 0.008);
                gain.gain.exponentialRampToValueAtTime(0.001, t0 + note.dur);
                oscillator.connect(gain);
                gain.connect(ctx.destination);
                oscillator.start(t0);
                oscillator.stop(t0 + note.dur + 0.02);
            }
        } catch {
            // Audio glitches must never break the flow.
        }
    }, []);

    const toggleMute = useCallback(() => {
        setMuted((current) => {
            const next = !current;
            try {
                localStorage.setItem(MUTED_STORAGE_KEY, String(next));
            } catch {
                // Storage unavailable (private mode) — mute only applies for this session
            }
            if (next) {
                queueRef.current = [];
                clearTimers();
                speakingRef.current = false;
                if (synthAvailable()) {
                    window.speechSynthesis.cancel();
                }
            }
            return next;
        });
    }, [clearTimers]);

    // Chrome returns an empty voice list until 'voiceschanged' fired — load it up front.
    useEffect(() => {
        if (!synthAvailable()) {
            return undefined;
        }
        const synth = window.speechSynthesis;
        const loadVoices = () => {
            voicesRef.current = synth.getVoices?.() ?? [];
        };
        loadVoices();
        synth.addEventListener?.('voiceschanged', loadVoices);
        return () => synth.removeEventListener?.('voiceschanged', loadVoices);
    }, []);

    // Leaving the screen must stop pending/running audio.
    useEffect(
        () => () => {
            clearTimers();
            queueRef.current = [];
            if (synthAvailable()) {
                window.speechSynthesis.cancel();
            }
            audioCtxRef.current?.close?.().catch?.(() => undefined);
        },
        [clearTimers],
    );

    return { speak, playEarcon, prime, muted, toggleMute };
};
